/**
 * Dragging a wall's end handle, expressed as a TYPED LENGTH EDIT.
 *
 * The handle used to move freely in 2D, which re-angled the wall and every
 * wall meeting the moved junction. Here the cursor is projected onto the
 * wall's own axis and supplies nothing but a length delta; from that point on
 * it is the identical edit to typing the number — `computeStretch`, the
 * draggable translate-rectangle, the locks, the storey scope and the same
 * Confirm. The wall's angle is preserved by construction because that is what
 * the typed pathway guarantees.
 *
 * Two pure pieces live here: how far the drag lengthens the wall, and where
 * the rectangle's two `dir`-edges have to sit for the end the user GRABBED to
 * be the end that moves.
 *
 * No Firestore, no React.
 */

import { ENDPOINT_TOL, wallEndpointsXZ } from './wallDisplacement';

/**
 * How much the drag lengthens the wall, in metres.
 *
 * The cursor is projected onto the wall's own axis, measured from the FIXED
 * end (the one the user did not grab). Positive means the grabbed end moved
 * away from the fixed end, i.e. the wall got longer — regardless of which end
 * was grabbed or which way the wall happens to be wound.
 *
 * A DELTA, not an absolute length, deliberately: the handle sits on whichever
 * wall is selected, while the measurement being edited belongs to a room wall
 * that is often a shorter sub-segment of it. The delta applies to either.
 *
 * @param {Object} wall - the wall as it stood when the gesture began.
 * @param {'start'|'end'} handle - which endpoint is being dragged.
 * @param {{x:number,z:number}} cursor - cursor position in world XZ.
 * @returns {number} signed length change in metres.
 */
export function wallLengthDelta(wall, handle, cursor) {
  if (!wall?.center || !(wall?.dimensions?.x > 0) || !cursor) return 0;
  const { a, b, length } = wallEndpointsXZ(wall);
  // `getWallEndPoints`' convention: 'start' is the `a` end.
  const grabbed = handle === 'start' ? a : b;
  const fixed = handle === 'start' ? b : a;
  const ux = (grabbed.x - fixed.x) / length;
  const uz = (grabbed.z - fixed.z) / length;
  const reach = (cursor.x - fixed.x) * ux + (cursor.z - fixed.z) * uz;
  return reach - length;
}

/**
 * Where the translate-rectangle's `dir`-edges go so that the GRABBED end is
 * the end that moves, and as little else as possible moves with it.
 *
 * `getMeasurementAxis` labels the edited wall's ends by their projection along
 * `dir`: `anchor` is the smaller, `mover` the larger. The rectangle must
 * contain exactly one of them (`computeStretch`'s one-sided rule), and it has
 * to be the one under the cursor — so grabbing the mover cuts with the box's
 * NEAR edge, and grabbing the anchor cuts with its FAR edge (the box then
 * extends the other way, and `computeStretch` flips `delta`'s sign so the
 * anchor end still moves away from the static mover).
 *
 * **Where the cut goes along the wall.** Anywhere strictly between the wall's
 * two ends satisfies the one-sided rule, but the position decides how much of
 * the plan comes along: everything on the grabbed side of the cut moves —
 * junctions, and ALSO every child and free-standing object, which classify by
 * their own centre rather than by a junction. Putting the cut at the wall's
 * midpoint therefore drags along all the furniture in that half, which is not
 * what pulling one end of a wall should do.
 *
 * So the cut goes **right beside the grabbed end**: one `ENDPOINT_TOL` inside
 * it. That is the tolerance the junction graph clusters on, which makes it the
 * closest placement that is still safe — every point clustering with the
 * grabbed end stays on the moving side with it (a tighter cut could split that
 * cluster and disconnect the wall from its neighbour), while every genuinely
 * distinct junction, and everything standing in the room, stays put.
 *
 * On a wall too short to hold that inset, the cut falls back to the midpoint —
 * the only placement left that keeps one end on each side.
 *
 * @param {{anchor:{x,z}, mover:{x,z}, dir:{x,z}}} axis - from getMeasurementAxis.
 * @param {{x:number,z:number}} grabbedPoint - the endpoint under the cursor, in
 *   the coordinates of the wall the HANDLE is on (which may be the structure
 *   wall rather than the measured room wall).
 * @param {{minD:number, maxD:number}} bounds - structure extent along `dir`.
 * @param {number} margin - padding past the structure, so an edge stays grabbable.
 * @returns {{ boundary:number, farBoundary:number, grabbedIsMover:boolean }}
 */
export function boundaryForGrabbedEnd(axis, grabbedPoint, bounds, margin) {
  const proj = (p) => p.x * axis.dir.x + p.z * axis.dir.z;
  const anchorProj = proj(axis.anchor);
  const moverProj = proj(axis.mover);
  const grabbedProj = proj(grabbedPoint);
  // Nearest by projection rather than by identity: the handle may be on the
  // structure wall while the axis came from a shorter room wall, so the two
  // never share a coordinate exactly.
  const grabbedIsMover =
    Math.abs(grabbedProj - moverProj) <= Math.abs(grabbedProj - anchorProj);

  // Cut relative to the MEASUREMENT's own end on the grabbed side — that is
  // the end the one-sided rule tests, not the handle's position.
  const from = grabbedIsMover ? moverProj : anchorProj;
  const toward = grabbedIsMover ? anchorProj : moverProj;
  const span = Math.abs(toward - from);
  const inset = span > 2 * ENDPOINT_TOL ? ENDPOINT_TOL : span / 2;
  const cut = grabbedIsMover ? from - inset : from + inset;

  return grabbedIsMover
    ? { boundary: cut, farBoundary: bounds.maxD + margin, grabbedIsMover }
    : { boundary: bounds.minD - margin, farBoundary: cut, grabbedIsMover };
}
